<template>
  <ul>
    <li v-for="(item, index) in visibleItems" :key="`visible-${index}`" class="mb-16">
      <StepCard
        :variant="item.variant"
        :title="item.title"
        :content="item.content"
        :linkURL="item.linkURL"
        :linkTo="item.linkTo"
        :disabled="item.disabled"
        :modalComponent="item.modalComponent"
        :modalProps="item.modalProps"
        :useFullPageNavigation="item.useFullPageNavigation"
      />
    </li>

    <!-- Show more toggle -->
    <li
      v-if="hasCollapsibleItems"
      class="flex items-center gap-8 mt-24 mb-16"
      data-test-id="show-more-toggle-btn"
      @click="showMore = !showMore"
    >
      <button class="text-body text-grey-clickable">{{ buttonText }}</button>
      <CollapseSmallIcon :width="16" :height="16" :class="showMore ? 'rotate-0' : 'rotate-180'" color="#4B4C52" />
    </li>

    <!-- Collapsible items -->
    <template v-if="showMore">
      <li v-for="(item, index) in collapsibleItems" :key="`collapsible-${index}`" class="mb-16">
        <StepCard
          :variant="item.variant"
          :title="item.title"
          :content="item.content"
          :linkURL="item.linkURL"
          :linkTo="item.linkTo"
          :disabled="item.disabled"
          :modalComponent="item.modalComponent"
          :modalProps="item.modalProps"
          :useFullPageNavigation="item.useFullPageNavigation"
        />
      </li>
    </template>
  </ul>
</template>

<script lang="ts">
import type { RawLocation } from 'vue-router';
import StepCard from './StepCard.vue';
import CollapseSmallIcon from '../../graphics/icons/CollapseSmallIcon.vue';

type StepCardItem = {
  variant?: string;
  title?: string;
  content?: string;
  linkURL?: string;
  linkTo?: RawLocation;
  disabled?: boolean;
  modalComponent?: any;
  modalProps?: object;
  useFullPageNavigation?: boolean;
};

export default {
  name: 'StepCardList',
  components: {
    StepCard,
    CollapseSmallIcon,
  },
  props: {
    items: {
      type: Array as () => StepCardItem[],
      required: true,
      validator(value: StepCardItem[]) {
        return value.every(
          (item) =>
            item &&
            typeof item === 'object' &&
            (item.variant === undefined || typeof item.variant === 'string') &&
            (item.title === undefined || typeof item.title === 'string') &&
            (item.content === undefined || typeof item.content === 'string') &&
            (item.linkURL === undefined || typeof item.linkURL === 'string') &&
            (item.linkTo === undefined || typeof item.linkTo === 'string' || typeof item.linkTo === 'object') &&
            (item.disabled === undefined || typeof item.disabled === 'boolean') &&
            (item.modalComponent === undefined || ['function', 'object'].includes(typeof item.modalComponent)) &&
            (item.modalProps === undefined || typeof item.modalProps === 'object') &&
            (item.useFullPageNavigation === undefined || typeof item.useFullPageNavigation === 'boolean')
        );
      },
    },
    collapsibleFilter: {
      type: Function,
      default: null,
    },
  },
  data(): any {
    return {
      showMore: false,
    };
  },
  computed: {
    visibleItems(): StepCardItem[] {
      if (!this.collapsibleFilter) return this.items as StepCardItem[];
      return (this.items as StepCardItem[]).filter((item) => !this.collapsibleFilter!(item));
    },
    collapsibleItems(): StepCardItem[] {
      if (!this.collapsibleFilter) return [];
      return (this.items as StepCardItem[]).filter((item) => this.collapsibleFilter!(item));
    },
    hasCollapsibleItems(): boolean {
      return this.collapsibleItems.length > 0;
    },
    buttonText(): string {
      return this.showMore ? 'Show less' : 'Show more';
    },
  },
};
</script>
